<template>
  <div class="question-chart">
    <apexchart
      v-if="getApexOptions({ questionId, indexChart }) !== null"
      :type="chartConfig.type === 'combo' ? 'line' : chartConfig.type"
      :options="getApexOptions({ questionId, indexChart })"
      :series="apexSeries"
      class="question-chart__chart"
    />

    <div class="question-chart__settings">
      <div
        v-if="chartConfig.type === 'bar' || configTypeComputed(chartConfig) === 'combo'"
        class="question-chart__settings-title"
      >
        {{ $tr('common.navigation.settings') }}
      </div>

      <div class="question-chart__setting">
        <trigger
          v-if="chartConfig.type === 'bar' || configTypeComputed(chartConfig) === 'combo'"
          :value="
            getApexOptions({ questionId, indexChart }) !== null &&
            getApexOptions({ questionId, indexChart }).chart !== undefined &&
            getApexOptions({ questionId, indexChart }).chart.stacked !== undefined
              ? getApexOptions({ questionId, indexChart }).chart.stacked
              : false
          "
          :text="$tr('graph.stack')"
          small
          @input="CHANGE_GRAPH_OPTION_CHART({ questionId, indexChart, property: 'stacked', value: $event })"
        />
      </div>

      <div class="question-chart__setting">
        <trigger
          v-if="
            configTypeComputed(chartConfig) === 'combo' ||
            chartConfig.type === 'pie' ||
            chartConfig.type === 'donut' ||
            chartConfig.type === 'bar'
          "
          :value="
            getApexOptions({ questionId, indexChart }) !== null &&
            getApexOptions({ questionId, indexChart }).chart !== undefined &&
            getApexOptions({ questionId, indexChart }).chart.stackType !== undefined
              ? getApexOptions({ questionId, indexChart }).chart.stackType === '100%'
                ? true
                : false
              : false
          "
          text="100%"
          small
          @input="
            CHANGE_GRAPH_OPTION_CHART({
              questionId,
              indexChart,
              property: 'stackType',
              value: $event === true ? '100%' : 'normal'
            })
          "
        />
      </div>
    </div>

    <div class="question-chart__series-wrapper">
      <div class="question-chart__series-title">
        <div>{{ $tr('graph.graphs') }}</div>

        <vbutton
          v-if="chartConfig.type !== 'pie' && chartConfig.type !== 'donut'"
          label="actions.add"
          :disabled="apexSeries.length > 5"
          size="small"
          @click="addGraph"
        />
      </div>

      <div v-if="chartConfig.type === 'pie' || chartConfig.type === 'donut'" class="question-chart__series">
        <div class="question-chart__series-item">
          <div class="question-chart__series-item-data-title">{{ $tr('graph.data') }}</div>

          <div class="question-chart__series-data">
            <div v-for="(item, indexData) in apexSeries" :key="indexData" class="question-chart__series-data-item">
              <input
                v-if="!isDataVariable({ questionId, indexChart, indexSeries: null, indexData })"
                :value="item"
                type="number"
                @input="
                  UPDATE_GRAPH_DATA_PIE({
                    questionId,
                    indexChart,
                    indexData,
                    newValue: Number($event.target.value)
                  })
                "
              />

              <input
                v-else
                :value="isDataVariable({ questionId, indexChart, indexSeries: null, indexData })"
                disabled
              />

              <question-variables-menu
                v-if="questionVariables && questionVariables.length > 0"
                :current-variable="isDataVariable({ questionId, indexChart, indexSeries: null, indexData })"
                :question-variables="questionVariables"
                class="question-chart__series-data-vars"
                @click="
                  ADD_GRAPH_VARIABLE({
                    questionId,
                    indexChart,
                    indexSeries: null,
                    indexData,
                    variableName: $event
                  })
                "
                @remove="REMOVE_GRAPH_VARIABLE({ questionId, indexChart, indexSeries: null, indexData })"
              />

              <div
                class="question-chart__label-remove"
                @click="REMOVE_GRAPH_DATA_PIE({ questionId, indexChart, indexData })"
              >
                <i class="icon icon-recycle_bin"></i>
              </div>
            </div>
          </div>

          <input v-model="newData" type="number" />
          <vbutton
            label="actions.add"
            :disabled="apexSeries.length > 20 || newData.length === 0"
            size="small"
            @click="addDataPie()"
          />
        </div>
      </div>

      <div v-else class="question-chart__series">
        <div v-for="(item, indexSeries) in apexSeries" :key="indexSeries" class="question-chart__series-item">
          <div class="question-chart__series-item-title">
            <vinput :value="item.name" placeholder="Имя графика" border @input="updateGraph(indexSeries, $event)" />

            <div v-if="chartConfig.type === 'combo'" class="question-chart__series-types">
              <div class="question-chart__series-type-current">
                <span>{{ getGraphTypeName(item.type) }}</span>
                <i
                  class="icon icon-caret"
                  :class="{ active: isTypesGraphShow === indexSeries }"
                  @click="toggleTypeGraphMenu(indexSeries)"
                ></i>
              </div>

              <div v-show="isTypesGraphShow === indexSeries" class="question-chart__series-types-menu">
                <div
                  v-for="itemType in typesGraph"
                  :key="itemType.type"
                  class="question-chart__series-type"
                  @click="changeComboType({ questionId, indexChart, indexSeries, newType: itemType.type })"
                >
                  {{ itemType.name }}
                </div>
              </div>
            </div>

            <div
              v-if="apexSeries.length > 1"
              class="question-chart__series-item-remove"
              @click="removeGraph(indexSeries)"
            >
              <i class="icon icon-recycle_bin"></i>
            </div>
          </div>

          <div class="question-chart__series-item-data-title">{{ $tr('graph.data') }}</div>

          <div class="question-chart__series-data">
            <div v-for="(itemData, indexData) in item.data" :key="indexData" class="question-chart__series-data-item">
              <input
                v-if="!isDataVariable({ questionId, indexChart, indexSeries, indexData })"
                type="number"
                :value="item.data[indexData]"
                @input="
                  UPDATE_GRAPH_DATA({
                    questionId,
                    indexChart,
                    indexSeries,
                    indexData,
                    newValue: Number($event.target.value)
                  })
                "
              />

              <input v-else :value="isDataVariable({ questionId, indexChart, indexSeries, indexData })" disabled />

              <question-variables-menu
                v-if="questionVariables && questionVariables.length > 0"
                :current-variable="isDataVariable({ questionId, indexChart, indexSeries, indexData })"
                :question-variables="questionVariables"
                class="question-chart__series-data-vars"
                @click="
                  ADD_GRAPH_VARIABLE({
                    questionId,
                    indexChart,
                    indexSeries,
                    indexData,
                    variableName: $event
                  })
                "
                @remove="REMOVE_GRAPH_VARIABLE({ questionId, indexChart, indexSeries, indexData })"
              />

              <div
                class="question-chart__label-remove"
                @click="REMOVE_GRAPH_DATA({ questionId, indexChart, indexSeries, indexData })"
              >
                <i class="icon icon-recycle_bin"></i>
              </div>
            </div>
          </div>

          <input v-model="newData[indexSeries]" type="number" />
          <vbutton
            label="actions.add"
            :disabled="
              apexSeries.length > 20 || (newData[indexSeries] !== undefined && newData[indexSeries].length === 0)
            "
            size="small"
            @click="addNewData(indexSeries)"
          />
        </div>
      </div>
    </div>

    <div class="question-chart__labels-wrapper">
      <div class="question-chart__labels-title">{{ $tr('graph.labels') }}</div>

      <div class="question-chart__labels">
        <div v-for="(item, indexLabel) in chartConfig.labels" :key="indexLabel" class="question-chart__label">
          <vinput
            :value="mergedLabel(chartConfig.labels[indexLabel])"
            class="question-chart__label-input"
            border
            @input="UPDATE_GRAPH_LABEL({ questionId, indexChart, indexLabel, value: $event })"
          />

          <div class="question-chart__label-remove" @click="REMOVE_GRAPH_LABEL({ questionId, indexChart, indexLabel })">
            <i class="icon icon-recycle_bin"></i>
          </div>
        </div>
      </div>

      <div class="question-chart__labels-add">
        <vinput v-model="newLabel" border />

        <vbutton label="actions.add" :disabled="newLabel.length < 1" size="small" @click="addLabel" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import Trigger from '@/components/shared/Trigger';
import QuestionVariablesMenu from '@/routes_new/inner/vacancy/tests/components/QuestionVariablesMenu.vue';

export default {
  name: 'VacancyTestQuestionChart',

  components: {
    Trigger,
    QuestionVariablesMenu
  },

  props: {
    questionId: {
      type: [Number, String],
      default: 0
    },

    indexChart: {
      type: Number,
      default: 0
    },

    chartConfig: {
      type: Object,
      default: () => {}
    },

    questionVariables: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      isHundredPercentGraph: false,
      newLabel: '',
      newData: [''],
      typesGraph: [
        { type: 'line', name: this.$tr('graph.line') },
        { type: 'area', name: this.$tr('graph.area') },
        { type: 'column', name: this.$tr('graph.bar') }
      ],

      isTypesGraphShow: null // indexChart or null
    };
  },

  computed: {
    ...mapGetters('vacancyTests', ['isDataVariable', 'getApexOptions', 'generateVariableValue']),

    apexSeries() {
      let series = this.chartConfig.series.slice(0);

      if (this.chartConfig.type === 'pie' || this.chartConfig.type === 'donut') {
        for (let index = 0; index < series.length; index++) {
          const isVariable = this.isDataVariable({
            questionId: this.questionId,
            indexChart: this.indexChart,
            indexSeries: null,
            indexData: index
          }); // returns false or variable name

          if (isVariable) {
            const variable = this.questionVariables.find(x => x.name == isVariable);

            if (variable !== undefined) {
              series[index] = this.generateVariableValue(variable);
            }
          }
        }
      } else {
        for (let index = 0; index < series.length; index++) {
          let data = [];

          data = series[index].data;

          for (let indexData = 0; indexData < data.length; indexData++) {
            const isVariable = this.isDataVariable({
              questionId: this.questionId,
              indexChart: this.indexChart,
              indexSeries: index,
              indexData
            }); // returns false or variable name

            if (isVariable) {
              const variable = this.questionVariables.find(x => x.name == isVariable);

              if (variable !== undefined) {
                series[index].data[indexData] = this.generateVariableValue(variable);
              }
            }
          }
        }
      }

      return series;
    }
  },

  created() {
    this.newLabel = '';
    this.newData.push('');
  },

  methods: {
    ...mapMutations('vacancyTests', [
      'UPDATE_GRAPH_NAME',
      'ADD_GRAPH',
      'REMOVE_GRAPH',
      'ADD_GRAPH_DATA',
      'UPDATE_GRAPH_DATA',
      'REMOVE_GRAPH_DATA',
      'ADD_GRAPH_DATA_PIE',
      'UPDATE_GRAPH_DATA_PIE',
      'REMOVE_GRAPH_DATA_PIE',
      'ADD_GRAPH_VARIABLE',
      'REMOVE_GRAPH_VARIABLE',
      'ADD_GRAPH_LABEL',
      'UPDATE_GRAPH_LABEL',
      'REMOVE_GRAPH_LABEL',
      'CHANGE_GRAPH_TYPE_COMBO',
      'CHANGE_GRAPH_OPTION_CHART'
    ]),

    getGraphTypeName(type) {
      const typeIndex = this.typesGraph.findIndex(x => x.type === type);

      if (typeIndex >= 0) {
        return this.typesGraph[typeIndex].name;
      }

      return this.typesGraph[0].name;
    },

    configTypeComputed(config) {
      const isComboSeries = config.series.some(x => x.type !== undefined && x.type !== null);

      if (config.type === 'line' && isComboSeries) {
        return 'combo';
      }

      return config.type;
    },

    toggleTypeGraphMenu(indexSeries) {
      if (this.isTypesGraphShow === null) {
        this.isTypesGraphShow = indexSeries;
      } else {
        this.isTypesGraphShow = null;
      }
    },

    mergedLabel(label) {
      if (Array.isArray(label)) {
        return label.join(' ');
      }

      return label;
    },

    changeComboType(data) {
      this.CHANGE_GRAPH_TYPE_COMBO(data);
      this.isTypesGraphShow = null;
    },

    // graph
    addGraph() {
      this.ADD_GRAPH({ questionId: this.questionId, indexChart: this.indexChart });
      this.newData.push('');
    },

    updateGraph(indexSeries, value) {
      this.UPDATE_GRAPH_NAME({
        questionId: this.questionId,
        indexChart: this.indexChart,
        indexSeries,
        newName: value
      });
    },

    removeGraph(indexSeries) {
      this.REMOVE_GRAPH({ questionId: this.questionId, indexChart: this.indexChart, indexSeries });
      this.newData.splice(indexSeries, 1);
    },

    // graph data
    addNewData(indexSeries) {
      let newValue = this.newData[indexSeries];

      if (typeof Number(this.newData[indexSeries]) === 'number') {
        newValue = Number(this.newData[indexSeries]);
      }

      this.ADD_GRAPH_DATA({ questionId: this.questionId, indexChart: this.indexChart, indexSeries, newValue });

      if (
        this.chartConfig !== null &&
        this.chartConfig.labels.length < this.chartConfig.series[indexSeries].data.length
      ) {
        this.ADD_GRAPH_LABEL({
          questionId: this.questionId,
          indexChart: this.indexChart,
          newLabel: this.chartConfig.series[indexSeries].data.length
        });
      }

      this.newData[indexSeries] = '';
    },

    addDataPie() {
      let newValue = this.newData;

      if (typeof Number(this.newData) === 'number') {
        newValue = Number(this.newData);
      }

      this.ADD_GRAPH_DATA_PIE({ questionId: this.questionId, indexChart: this.indexChart, newValue });

      if (this.chartConfig !== null && this.chartConfig.labels.length < this.chartConfig.series.length) {
        this.ADD_GRAPH_LABEL({
          questionId: this.questionId,
          indexChart: this.indexChart,
          newLabel: this.chartConfig.series.length
        });
      }

      this.newData = '';
    },

    // labels
    addLabel() {
      this.ADD_GRAPH_LABEL({ questionId: this.questionId, indexChart: this.indexChart, newLabel: this.newLabel });
      this.newLabel = '';
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

// hide arrows from number
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  appearance: textfield;
  -moz-appearance: textfield;
}

.question-chart {
  padding: 20px 0;

  label {
    @include mixin-text-tag;
  }

  &__chart {
    margin-bottom: 20px;
  }

  // settings
  &__settings {
    margin-bottom: 30px;
  }

  &__setting {
    margin-bottom: 8px;
  }

  &__settings-title {
    @include mixin-text-header;
    margin-bottom: 15px;
  }

  // labels
  &__labels-wrapper {
    margin-bottom: 20px;
  }

  &__labels {
    display: flex;
    flex-wrap: wrap;
  }

  &__labels-title {
    @include mixin-text-header;
    margin-bottom: 15px;
  }

  &__label {
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    width: 48%;
    margin-right: 2%;
  }

  &__label-input {
    flex: 1;
    width: 0; // for flex: 1
  }

  &__label-remove {
    margin-left: 5px;
    height: 100%;
    flex: 0 0 30px;
    font-size: 22px;
    color: $grey;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
      color: $red;
    }
  }

  &__labels-add {
    display: flex;
    align-items: center;
  }

  &__label-group {
    display: flex;
  }

  // series
  &__series-wrapper {
    margin-bottom: 30px;
  }

  &__series-title {
    @include mixin-text-header;
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  &__series-item {
    border: 1px solid $grey-light;
    padding: 15px;
    margin-bottom: 10px;
    border-radius: 3px;
  }

  &__series-item-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 10px;
    border-bottom: 1px solid $grey-light;
    margin-bottom: 10px;
  }

  &__series-types {
    position: relative;
    margin-left: auto;
  }

  &__series-types-menu {
    position: absolute;
    top: 30px;
    right: 0;
    padding: 5px 0;
    background: $white;
    box-shadow: $box-shadow;
    border-radius: 4px;
    z-index: 10;
  }

  &__series-type-current {
    @include mixin-text-main;
    color: $grey;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    height: 100%;
    text-transform: capitalize;
    transition: background 0.3s;

    span {
      margin-right: 10px;
    }

    i {
      transform: rotate(0deg);
      transition: transform 0.2s;
    }

    i.active {
      transform: rotate(180deg);
      transition: transform 0.2s;
    }
  }

  &__series-type {
    @include mixin-text-main;
    padding: 5px 20px;
    cursor: pointer;
    transition: all 0.3s;

    &.active {
      background: $blue-light-dark;
      color: $white;
    }

    &:hover {
      background: lighten($blue-light, 10%);
      color: $white;
      transition: all 0.3s;
    }
  }

  &__series-item-remove {
    margin-left: 5px;
    margin-bottom: 3px;
    height: 30px;
    width: 30px;
    font-size: 22px;
    color: $grey;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
      color: $red;
    }
  }

  &__series-item-data-title {
    @include mixin-text-header;
    margin-bottom: 10px;
  }

  &__series-data {
    display: flex;
    flex-wrap: wrap;
  }

  &__series-data-vars {
    position: absolute;
    top: 0;
    right: 35px;
  }

  &__series-data-item {
    margin-right: 10px;
    margin-bottom: 7px;
    max-width: 100px;
    max-width: 120px;
    display: flex;
    position: relative;

    input {
      width: 100%;
    }
  }
}
</style>
